import { useMutation } from '@apollo/client'
import { Button, Col, Form, Input, InputNumber, message, Row, Spin } from 'antd'
import * as PropTypes from 'prop-types'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLoginModal } from '../../../../../context/loginModalContext'
import { showAuthError } from '../../../../../shared/utils/webHelpers'
import { StyledDrawer } from '../../../../common/CommonStyles'
import { v4 as uuidv4 } from 'uuid'
import { useForceUpdate } from '../../../../../shared/hooks/useForceUpdate'
import CreateNewItemExtraButtons from '../../../../common/CreateNewItemExtraButtons'
import { useNavigate, useParams } from 'react-router-dom'
import NameField from '../../../../common/formFields/NameField'
import ADD_NEW_REFERRAL_SUB_SOURCE
  from '@dentolize/clinic-mobile/src/shared/store/mutations/actions/referralSubSources/addNewReferralSubSource'
import EDIT_REFERRAL_SUB_SOURCE
  from '@dentolize/clinic-mobile/src/shared/store/mutations/actions/referralSubSources/editReferralSubSource'
import UserColorPicker from '../../Users/UserColorPicker'
import DeleteReferralSubSourceButton from './DeleteReferralSubSourceButton'

const ReferralSubSourceForm = props => {
  const [forceUpdate, handleForceUpdate] = useForceUpdate()

  const navigate = useNavigate()
  const [form] = Form.useForm()
  const { setLoginModalVisible } = useLoginModal()
  const { t } = useTranslation()
  const { referralSource } = useParams()

  const { formVisible, selectedItem, handleClose } = props
  const { validateFields, resetFields, setFieldsValue } = form

  useEffect(() => {
    if (formVisible) {
      resetFields()
      if (selectedItem.new) {
        setFieldsValue({
          color: `#${Math.floor(Math.random() * 16777215).toString(16)}`
        })
      }
    }
  }, [formVisible])

  const [addNewItem, { loading }] = useMutation(ADD_NEW_REFERRAL_SUB_SOURCE)
  const [editItem, { loading: loadingEdit }] = useMutation(EDIT_REFERRAL_SUB_SOURCE)

  const generatedID = useMemo(uuidv4, [formVisible, forceUpdate])

  const handleSubmit = key => {
    validateFields()
      .then(async values => {
        try {
          let item

          if (selectedItem.new) {
            item = await addNewItem({
              variables: {
                ...values,
                referralSource: referralSource || selectedItem.referralSource,
                generatedID
              }
            })

            if (key === 'view') {
              navigate(`/settings/types/referral-sub-source/${generatedID}`)
              handleClose()
            } else if (key === 'new') {
              resetFields()
              handleForceUpdate()
              setFieldsValue({
                color: `#${Math.floor(Math.random() * 16777215).toString(16)}`
              })
            } else {
              handleClose(item.data.addNewReferralSubSource)
            }
          } else {
            item = await editItem({
              variables: {
                ...values,
                referralSubSource: selectedItem.id
              }
            })
            handleClose()
          }

          message.success(`${selectedItem.new ? t('common.createdSuccessfully') : t('common.updatedSuccessfully')}`)
          if (props.handleAnimateRow) {
            props.handleAnimateRow(
              (item.data.addNewReferralSubSource && item.data.addNewReferralSubSource.id) ||
                (item.data.editReferralSubSource && item.data.editReferralSubSource.id),
              selectedItem.new
            )
          }
        } catch (e) {
          showAuthError(e, t, setLoginModalVisible)
        }
      })
      .catch(({ errorFields }) => {
        form.scrollToField(errorFields?.[0]?.name)
      })
  }

  return (
    <StyledDrawer
      title={!selectedItem.new ? t('common.edit') : t('patients.newItem')}
      open={formVisible}
      onClose={handleClose}
      destroyOnClose
      footer={
        <div className="flex-container justify-between">
          {!selectedItem.new ? (
            <DeleteReferralSubSourceButton
              selectedItem={selectedItem}
              handleAnimateRow={props.handleAnimateRow}
              handleClose={handleClose}
            />
          ) : selectedItem.new && selectedItem.fromDrawer ? (
            <CreateNewItemExtraButtons handleSubmit={handleSubmit} disabled={loading} />
          ) : null}
          <Button loading={loading || loadingEdit} htmlType="submit" type="primary" form={`${generatedID}referralSubSourceForm`}>
            {selectedItem.new ? t('common.create') : t('common.update')}
          </Button>
        </div>
      }
    >
      <Spin spinning={loading || loadingEdit}>
        <Form
          layout="vertical"
          scrollToFirstError
          form={form}
          name={`${generatedID}referralSubSourceForm`}
          onFinish={handleSubmit}
          initialValues={selectedItem}
        >
          <Row type="flex" gutter={16}>
            <Col span={16}>
              <NameField required min={1} max={100} autoFocus autoComplete="" />
            </Col>
            <Col span={8}>
              <Form.Item name="color" label={t('settings.groups.color')}>
                <UserColorPicker />
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <Form.Item label={t('app.referralUrl')} name="referralUrl">
                <Input maxLength={500} />
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <Form.Item label={t('app.referralId')} name="referralId">
                <Input maxLength={500} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="order" label={t('app.order')}>
                <InputNumber min={0} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </StyledDrawer>
  )
}

ReferralSubSourceForm.propTypes = {
  selectedItem: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  formVisible: PropTypes.bool.isRequired,
  handleAnimateRow: PropTypes.func
}

export default ReferralSubSourceForm
